import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import ProfileAvatar from './ProfileAvatar';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const { user, logout, avatarKey } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const menuItems = [
    { path: '/dashboard', icon: '🏠', key: 'dashboard' },
    { path: '/wallet', icon: '💰', key: 'wallet' },
    { path: '/friends', icon: '👥', key: 'friends' },
    { path: '/chat', icon: '💬', key: 'chat' },
    { path: '/history', icon: '📊', key: 'history' },
    { path: '/profile', icon: '👤', key: 'profile' },
  ];

  if (!user) {
    return null;
  }

  return (
    <>
      <div
        className={`sidebar-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/dashboard" className="sidebar-logo" onClick={onClose}>
            <img src="/logo.png" alt="Tellcrypt" className="sidebar-logo-icon" style={{ width: '32px', height: '32px' }} />
            <span className="sidebar-logo-text">Tellcrypt</span>
          </Link>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={onClose}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              <span className="sidebar-nav-text">{t(`navigation.${item.key}`)}</span>
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
              <ProfileAvatar user={user} size={40} onClick={() => { navigate('/profile'); onClose?.(); }} cacheBuster={avatarKey} />
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div className="sidebar-user-name">
                  {user.firstName} {user.lastName}
                </div>
                <div className="sidebar-user-email">
                  {user.email}
                </div>
              </div>
            </div>
          </div>

          <div className="sidebar-language">
            <LanguageSelector />
          </div>

          <button
            onClick={handleLogout}
            className="sidebar-logout-btn"
          >
            <span>🚪</span>
            <span>{t('auth.logout')}</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
